import { useQuery } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useLocalStorage } from "react-use";
import { useAuthToken } from "../State/Hooks/authHooks";
import { useAppDispatch, useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue, toggleLoading, updateClient } from "../State/Slices/SelectedClientSlice";
import { AppDispatch } from "../State/Store";
import { IClient, IClientCustomField, IClientCustomFieldData } from "../Typings/Client";
import { secureFetcher } from "./queryHelpers";
import { useCurrentUser } from "./UserQueries";

interface IAllClientsQuery {
  clients: IClient[];
  loading: boolean;
}

interface IUpdateSelectedClient {
  clientList: IClient[];
  lastClientId: string | undefined;
  setLastClientId: Dispatch<SetStateAction<string | undefined>>;
  dispatch: AppDispatch;
}

export function useAllClients<T = IClient[]>(enabled = true, select?: (data: IClient[]) => T) {
  const token = useAuthToken();
  return useQuery<IClient[], unknown, T>(["allClients"], () => secureFetcher("/clients", token), {
    enabled: enabled && !!token,
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    select,
  });
}

export function useManagementClients(enabled = true) {
  const token = useAuthToken();
  return useQuery<IClient[]>(["managementClients"], () => secureFetcher("/clients/management", token), {
    enabled: enabled && !!token,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useClients(): IAllClientsQuery {
  const dispatch = useAppDispatch();
  const user = useCurrentUser();
  const selectedClient = useAppSelector(selectClientValue);
  const [lastClientId, setLastClientId] = useLocalStorage("lastClientId", selectedClient.clientId);
  const { data: clientList, isInitialLoading } = useAllClients(!!user.id);
  useEffect(() => {
    const dataReady = clientList?.length && !isInitialLoading;
    if (dataReady && selectedClient.clientId == null) {
      updateSelectedClient({ lastClientId, clientList, dispatch, setLastClientId });
    }
  }, [lastClientId, selectedClient.clientId, clientList, isInitialLoading]);
  return { clients: clientList || [], loading: isInitialLoading };
}

export function useSelectedClient() {
  const selectedClient = useAppSelector(selectClientValue);
  return useAllClients<IClient>(
    !!selectedClient.clientId,
    (data) => data.find((c) => c.clientId === selectedClient.clientId) ?? selectedClient,
  );
}

export function useSelectedClientCustomFields() {
  const selectedClient = useAppSelector(selectClientValue);
  return useAllClients<IClientCustomField[]>(
    !!selectedClient.clientId,
    (data) =>
      data.find((c) => c.clientId === selectedClient.clientId)?.customFields ?? selectedClient.customFields,
  );
}

function updateSelectedClient(options: IUpdateSelectedClient) {
  const { lastClientId, clientList, dispatch, setLastClientId } = options;
  if (lastClientId) {
    const client = clientList.find((client) => client.clientId === lastClientId);
    if (client) {
      setLastClientId(client.clientId);
      dispatch(updateClient(client));
      dispatch(toggleLoading(false));
      return;
    }
  }
  if (clientList.length) {
    setLastClientId(clientList[0].clientId);
    dispatch(updateClient(clientList[0]));
    dispatch(toggleLoading(false));
  }
}

export function useClientCustomFieldData() {
  const selectedClient = useAppSelector(selectClientValue);
  const token = useAuthToken();
  return useQuery<IClientCustomFieldData>(
    ["clientCustomFieldData", selectedClient.clientId],
    () => secureFetcher(`/clients/customdata/${selectedClient.clientId}`, token),
    {
      enabled: !!selectedClient.clientId && !!token,
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
  );
}
